import axios from 'axios';
import {observable} from 'mobx';
import pkceChallenge from 'pkce-challenge';
import {web_api_entrypoint, oauth_client_id} from './consts.js';
import {show_loading_screen} from './loading.js';
import {hash} from './url.js';
import {getItem, setItem} from './storage.js';
import {is_whitelabel} from './consts.js';
import {whitelabel_info} from './whitelabel.js';
import { sessionManager } from '@v2cloud/telemetry';
import { logEvent } from './log';
import { requestIdOf } from './log/correlation';
import { oauthErrorCode } from './log/clientEventSchema';

const PING_FLAG = 'epic3733_ping';

async function apply_ping_tuning(getObjectFlagValue) {
  const tuning = await getObjectFlagValue(PING_FLAG, null);
  if (!tuning) return;

  sessionManager.setActiveIntervalMs(tuning.activeIntervalMs);
  sessionManager.setVisibleIntervalMs(tuning.visibleIntervalMs);
  sessionManager.setBackgroundIntervalMs(tuning.backgroundIntervalMs);
  sessionManager.setPingBudgetPerSec(tuning.pingBudgetPerSec);
  sessionManager.setMaxConcurrentProbes(tuning.maxConcurrentProbes);
  sessionManager.setScheduleJitter(tuning.scheduleJitter);
  sessionManager.setPingsFlushIntervalMs(tuning.pingsFlushIntervalMs);
}

const config = {};

config.api_endpoint = web_api_entrypoint;


if(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {

  config.api_endpoint = '/';

}

if(!getItem('api_endpoint')) {
  setItem('api_endpoint', '');
}

window.getItem = getItem;
window.setItem = setItem;

window.axios = axios;

axios.defaults.headers.common['Authorization'] = getItem('access_token') || '';
axios.defaults.baseURL = getItem('api_endpoint') || config.api_endpoint
axios.defaults.timeout = 60000;

sessionManager.setRequestHeaders(() => {
  const auth = axios.defaults.headers.common['Authorization'];
  return auth ? { Authorization: auth } : {};
});

sessionManager.setApiBaseUrl(axios.defaults.baseURL);

sessionManager.setWhitelabel(is_whitelabel);

// Clear access token. It gets reset on close if the user selects the persistent option.
// If the application force-closes, the token will be lost.
window.isElectron && setItem('access_token', '');


const persist_access_token = () => {

  setItem('access_token', axios.defaults.headers.common['Authorization']);

};

window.persist_access_token = persist_access_token;


export const oauth_web_api_entrypoint = getItem('api_endpoint') || (web_api_entrypoint || 'https://dash-alpha.v2cloud.com');
export const oauth_client_id_ = getItem('oauth_client_id') || (oauth_client_id || 'aQOCShKtMVsKTGs0ZSFgXFOiAuS9ezM9hSVCdMZh');

export const tokyo_api_endpoint = config.api_endpoint;


export const logged_in = observable.box();

// When this page signed in, for the session length reported with a forced logout.
let logged_in_at = null;
export const auth_error = observable.box();

window.logged_in = logged_in;



const get_relative_oauth_url = () => {

	const {code_verifier, code_challenge} = pkceChallenge();
	window.localStorage.setItem('code_verifier', code_verifier);

	const oauth_state_csrf_token = Math.random().toString().slice(2);
	window.localStorage.setItem('oauth_state_csrf_token', oauth_state_csrf_token);

  let next = ('/o/authorize/?response_type=code&client_id=' + oauth_client_id_ + '&state=' + oauth_state_csrf_token + '&code_challenge=' +
              code_challenge + '&code_challenge_method=S256&redirect_uri=')
  
	if(window.isElectron) {
		const port = window.api.get_localserver_port();
    next +=  'http://127.0.0.1:' + port + "?app_open=true" + window.location.hash;
	} else {
		const redirect_uri = is_whitelabel ? window.location.origin + '?wl/' : window.location.origin
		next +=  redirect_uri + window.location.hash;
	}

  return '/login/?next=' + encodeURIComponent(next);
};


export const get_oauth_url = () => {

  const custom_entrypoint = whitelabel_info.get() && whitelabel_info.get().endpoint;

  // oauth_web_api_entrypoint will be one of:
  // dash(-alpha|-beta|-cloud|).v2cloud.com or dash.cloudworkstations.com
  return ((is_whitelabel && custom_entrypoint) ? custom_entrypoint : oauth_web_api_entrypoint)  + get_relative_oauth_url();

}


export const extract_url_token = show_loading_screen(async () => {

  let url = new URL(window.location.href);

  if(!url.searchParams.has('code')) return;

  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');

  window.history.replaceState({} , undefined, window.location.origin + window.location.hash);

  if(state !== window.localStorage.getItem('oauth_state_csrf_token')) {
    logEvent('auth.oauth.state_mismatch');

    return;
  }


  // redirect_uri needs to match that of the original request to the oauth authorization page
  const redirect_uri = !window.isElectron ? (is_whitelabel? window.location.origin + '?wl/' : window.location.origin) : (window.location.origin + '?app_open=true')

  let response;
  try {
    response = await axios({
      url: oauth_web_api_entrypoint + '/o/token/',
      method: 'post',
      headers: {
        'content-type': 'application/x-www-form-urlencoded'
      },
      data: {
        grant_type: 'authorization_code',
        client_id: oauth_client_id_,
        code_verifier: window.localStorage.getItem('code_verifier'),
        redirect_uri,
        code,
      }
    });
  } catch(error) {
    const refusal = error.response && error.response.data && error.response.data.error;
    logEvent('auth.token_exchange.failed', error.response
      ? { status: error.response.status, error_code: oauthErrorCode(refusal) }
      : { error_code: 'network' });
    throw error;
  }

  axios.defaults.headers.common['Authorization'] = 'Bearer ' + response.data.access_token;
  logged_in_at = Date.now();

  // keep session token in browser
  !window.isElectron && persist_access_token();

});



// The token the session is using right now. Storage can't be trusted for this:
// it holds the whole "Bearer <token>" header value, and the desktop app clears
// it at startup.
const current_access_token = () =>
  (axios.defaults.headers.common['Authorization'] || '').trim().replace(/^bearer\s+/i, '');


const logout_action = async redirect_to_oauth_login => {

  // Queued failure events go while the token is still valid; never wait more than 2 seconds.
  if(window.flushClientEvents) {
    await Promise.race([
      window.flushClientEvents({ drain: true, deadlineMs: 2000 }),
      new Promise(resolve => setTimeout(resolve, 2000)),
    ]);
  }

  const access_token = current_access_token();

  if(access_token) {

    try {

      await axios({
        url: oauth_web_api_entrypoint + '/o/revoke_token/',
        method: 'post',
        headers: {
          'content-type': 'application/x-www-form-urlencoded'
        },
        data: {
          client_id: oauth_client_id_,
          token: access_token
        }
      });

    } catch(e) {
      console.error(e.message);
    }

  }

  setItem('access_token', '');
  setItem('refresh_token', '');

  axios.defaults.headers.common['Authorization'] = '';



  const custom_entrypoint = whitelabel_info.get() && whitelabel_info.get().endpoint;

  let logout_link = ((is_whitelabel && custom_entrypoint) ? custom_entrypoint : oauth_web_api_entrypoint) + '/logout/'

  if(redirect_to_oauth_login)
    logout_link += '?next=' + encodeURIComponent(get_relative_oauth_url());

  if(window.isElectron) {
    window.api.open_link_external(logout_link);
  } else {
    window.location.href = logout_link;
  }

};

// Used to inject logout on close; see main.js
window.logout_action = logout_action;

window.setWhitelabel = (value) => setItem('is_whitelabel_override', value);


export const logout = show_loading_screen(async () => {

  await logout_action(true);

  await test_login();

});


export const logout_no_api = () => {

  logged_in.set(false);
	hash.set('#/login');

};



// Tokyo answers 403 both for an expired token (its first auth class) and for a permission it
// denies. Asking who we are tells them apart: true while the session works, false when Tokyo
// refuses it too, null when it can't be told (no answer). A plain fetch, so this interceptor
// doesn't see it.
// At most 5 seconds; no answer counts as "can't be told".
const SESSION_CHECK_TIMEOUT_MS = 5000;

const check_session = async () => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SESSION_CHECK_TIMEOUT_MS);
  try {
    const base = axios.defaults.baseURL || '/';
    const response = await fetch(base.replace(/\/?$/, '/') + 'api/auth/me/', {
      headers: { Authorization: axios.defaults.headers.common['Authorization'] || '' },
      signal: controller.signal,
    });
    if (response.status === 200) return true;
    return response.status === 403 || response.status === 401 ? false : null;
  } catch (error) {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

// Requests failing together share one check; the next failure checks again.
let session_check = null;

const session_still_valid = () => {
  if (!session_check) session_check = check_session().finally(() => { session_check = null; });
  return session_check;
};


const report_forced_logout = (error, expired = false) => {
  const reason = expired ? 'expired'
    : error.response ? 'forbidden' : error.code === 'ECONNABORTED' ? 'timeout' : 'network';
  const url = error.config && error.config.url;
  logEvent('auth.session.forced_logout', {
    reason,
    path: url ? new URL(url, 'http://path.invalid/').pathname : undefined,
    status: error.response && error.response.status,
    minutes_since_login: logged_in_at === null ? undefined : Math.floor((Date.now() - logged_in_at) / 60000),
    request_id: requestIdOf(error),
  });
};


axios.interceptors.response.use(function(response) {

  // Any status code that lie within the range of 2xx cause this function to trigger
  // Do something with response data
  return response;

}, async function (error) {

  if(!error.response || error.response.status === 403) {

    let expired = false;
    if(error.response && logged_in.get()) {
      const valid = await session_still_valid();
      // A permission denied for this request: the user stays signed in.
      if(valid === true) return Promise.reject(error);
      expired = valid === false;
    }

    if(logged_in.get()) report_forced_logout(error, expired);
    logout_no_api();

  }

  // Any status codes that falls outside the range of 2xx cause this function to trigger
  // Do something with response error
  return Promise.reject(error);

});


window.addEventListener('online', () => {

  auth_error.set('');
  test_login();

});

window.addEventListener('offline', () => auth_error.set('No internet connection.'));


export const test_login = show_loading_screen(async () => {

  if(!window.navigator.onLine) {

    auth_error.set('No internet connection.');

    logout_no_api();

    return;

  }


  let response;

  try {

    response = await axios({
      url: '/api/vms/'
    });

  } catch(e) {

  }


  if(!response || !response.data || !response.data.results) {

    return false;

  } else {

    // on success

    auth_error.set('');

    // Telemetry init only reaches the backend in the desktop app, where the
    // local proxy forwards /api. On the web build the API is a separate origin,
    // so init 404s; skip it there to avoid an unhandled rejection.
    try {
      const { getFlagValue, getObjectFlagValue } = await import('./feature_flags.js');
      if (await getFlagValue(PING_FLAG)) {
        await apply_ping_tuning(getObjectFlagValue);
        await sessionManager.init("denver");
      }
    } catch (e) {
      console.error(e);
    }
    logged_in.set(true);

    return true;

  }

});
